import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router';
import { BookOpen, CheckCircle2, Circle, Clock, MonitorSmartphone, Settings, ShoppingCart, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DescriptionList,
  LicenseBadge,
  StatCard,
  formatDate,
} from '@shripos/ui';
import { api } from '../../lib/api.js';
import { rupees } from '../../lib/money.js';
import { can, kitchenOf, useSession } from '../../stores/session.js';
const Dashboard = lazy(() => import('../reports/Dashboard.jsx').then((m) => ({ default: m.Dashboard })));

function Step({ done, to, icon: Icon, children }) {
  return (
    <Link to={to} className="flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-slate-50">
      {done ? <CheckCircle2 className="size-5 text-emerald-600" /> : <Circle className="size-5 text-slate-300" />}
      <Icon className="size-5 text-slate-400" />
      <span className="text-sm text-slate-700">{children}</span>
    </Link>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const isManager = can(me, 'staff.manage');
  const canBill = can(me, 'pos.bill');
  // Cashiers go straight to billing; owners and managers land on this overview.
  const cashierOnly = canBill && !isManager && !can(me, 'reports.view');
  const today = useQuery({
    queryKey: ['today'],
    queryFn: () => api.get('/pos/today'),
    enabled: Boolean(me.outlet) && !cashierOnly && !can(me, 'reports.view'),
    refetchInterval: 60_000,
  });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get('/users'), enabled: isManager });
  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get('/devices'),
    enabled: can(me, 'devices.manage'),
  });
  const items = useQuery({
    queryKey: ['menu-items'],
    queryFn: () => api.get('/menu/items'),
    enabled: can(me, 'menu.edit'),
  });

  const dayStatus = useQuery({
    queryKey: ['day-status'],
    queryFn: () => api.get('/days/status'),
    enabled: Boolean(me.outlet) && can(me, 'shift.manage'),
  });

  if (cashierOnly && me.outlet) return <Navigate to="/pos" replace />;
  // Kitchen staff (and kitchen-display devices) start on the kitchen screen.
  const kitchenOnly = can(me, 'kds.use') && !canBill && !can(me, 'reports.view');
  if (me.outlet && kitchenOf(me).display && (kitchenOnly || me.device?.kind === 'kds'))
    return <Navigate to="/kds" replace />;

  const staffWithPin = users.data?.items.filter((u) => u.hasPin && !u.isOwner).length ?? 0;
  const activeDevices = devices.data?.items.filter((d) => d.status === 'active').length ?? 0;
  const menuItems = items.data?.items.length ?? 0;
  const s = today.data;
  const lic = me.license;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('home.greeting', { name: me.user.name.split(' ')[0] })}</h1>
          <p className="text-slate-500">
            {me.tenant.name}
            {me.outlet ? ` · ${me.outlet.name}` : ''}
          </p>
        </div>
        {canBill && me.outlet && (
          <Link to="/pos" className="shrink-0">
            <Button size="lg" icon={ShoppingCart}>
              {t('home.openBilling', 'Open billing')}
            </Button>
          </Link>
        )}
      </div>
      {dayStatus.data?.pending.length > 0 && (
        <Alert
          tone={dayStatus.data.mode === 'block' ? 'danger' : 'warning'}
          title={
            dayStatus.data.mode === 'block'
              ? t('home.blocked', 'Billing is blocked: close the previous day')
              : t('home.dayOpen', 'A business day is not closed yet')
          }
          action={
            <Link to="/cash">
              <Button size="sm" variant="secondary">
                {t('pos.closeTheDay', 'Close the day')}
              </Button>
            </Link>
          }
        >
          {dayStatus.data.pending.join(', ')} — {t('home.countDrawers', 'count the drawers and print the Z report.')}
        </Alert>
      )}

      {can(me, 'reports.view') && (
        <Suspense fallback={null}>
          <Dashboard me={me} />
        </Suspense>
      )}

      {s && (
        <section>
          <p className="mb-2 text-sm font-medium text-slate-500">
            {t('home.today', 'Today')} · {formatDate(s.businessDate)}
          </p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label={t('cash.sales', 'Sales')} value={rupees(s.totalPaise)} tone="brand" />
            <StatCard label={t('cash.bills', 'Bills')} value={s.billCount} />
            <StatCard label={t('home.avgBill', 'Average bill')} value={rupees(s.avgBillPaise)} />
            <StatCard
              label={t('pos.openOrders', 'Open orders')}
              value={s.openOrders}
              icon={Clock}
              hint={s.voidCount ? t('home.voidedToday', '{{count}} voided today', { count: s.voidCount }) : undefined}
            />
          </div>
          {Object.keys(s.payments).length > 0 && (
            <p className="mt-2 text-sm text-slate-600">
              {Object.entries(s.payments)
                .filter(([, v]) => v)
                .map(([k, v]) => `${k.toUpperCase()} ${rupees(v)}`)
                .join(' · ')}
            </p>
          )}
        </section>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {isManager && (
          <Card>
            <CardHeader title={t('home.setup')} />
            <CardBody className="space-y-1 p-2">
              <Step done={menuItems > 0} to="/menu" icon={BookOpen}>
                {t('home.stepMenu', 'Set up your menu, prices and GST')}
              </Step>
              <Step done={staffWithPin > 0} to="/staff" icon={Users}>
                {t('home.stepStaff')}
              </Step>
              <Step done={activeDevices > 0} to="/devices" icon={MonitorSmartphone}>
                {t('home.stepDevice')}
              </Step>
              <Step done={false} to="/settings" icon={Settings}>
                {t('home.stepSettings', 'Check GSTIN, FSSAI, UPI ID and receipt settings')}
              </Step>
            </CardBody>
          </Card>
        )}
        <Card>
          <CardHeader title={t('home.subscription', 'Subscription')} actions={<LicenseBadge status={lic.status} />} />
          <CardBody>
            <DescriptionList
              items={[
                [
                  t('home.plan', 'Plan'),
                  <span key="p" className="capitalize">
                    {lic.planCode}
                  </span>,
                ],
                [t('home.validUntil', 'Valid until'), formatDate(lic.expiryDate)],
                [
                  t('home.limits', 'Outlets / devices / users'),
                  `${lic.limits.maxOutlets} / ${lic.limits.maxDevices} / ${lic.limits.maxUsers}`,
                ],
                [
                  t('home.signedInVia', 'Signed in via'),
                  me.session.authMethod === 'pin'
                    ? t('home.viaPin', 'PIN on registered device')
                    : t('home.viaEmail', 'Email & password'),
                ],
                [
                  t('home.thisDevice', 'This device'),
                  me.device ? (
                    <Badge key="d" tone="success">
                      {me.device.name} ({me.device.code})
                    </Badge>
                  ) : (
                    t('home.notRegistered', 'Not a registered device')
                  ),
                ],
              ]}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
